import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";

const CONSENT_KEY = "ayeris_cookie_consent";

export const getCookieConsent = (): "accepted" | "rejected" | null => {
  return localStorage.getItem(CONSENT_KEY) as "accepted" | "rejected" | null;
};

const GA_ID = "G-7P0WXC125J";

/** Dynamically inject the AdSense script if not already present */
export const loadAdSenseScript = () => {
  if (document.querySelector('script[src*="adsbygoogle"]')) return;
  const s = document.createElement("script");
  s.src =
    "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5652998927875015";
  s.async = true;
  s.crossOrigin = "anonymous";
  document.head.appendChild(s);
};

/** Dynamically inject the GA4 gtag script */
const loadGtagScript = () => {
  if (document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`)) return;
  const s = document.createElement("script");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  s.async = true;
  document.head.appendChild(s);

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag(...args: any[]) { w.dataLayer.push(args); }
  w.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID);
};

/** Track a custom GA4 event (no-op if consent not given) */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  const w = window as any;
  if (typeof w.gtag === "function") {
    w.gtag("event", eventName, params);
  }
};

/** Load all consent-gated scripts (AdSense is loaded on-demand by AdSenseUnit) */
const loadConsentScripts = () => {
  loadGtagScript();
};

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    } else if (consent === "accepted") {
      loadConsentScripts();
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setVisible(false);
    loadConsentScripts();
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 inset-x-0 z-[9999] p-4"
        >
          <div className="max-w-lg mx-auto rounded-2xl border border-border bg-card/95 backdrop-blur-lg shadow-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Cookie className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="font-display text-sm font-semibold text-foreground">
                  {t("cookies.title")}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t("cookies.description")}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                className="flex-1 py-2.5 rounded-xl bg-secondary text-foreground font-display font-semibold text-sm border border-border active:scale-95 transition-transform"
              >
                {t("cookies.reject")}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform"
              >
                {t("cookies.accept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
